import { useEffect } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import type {
	AiStreamingOrchestratorPlugin,
	StreamingError,
} from './aiStreamingOrchestratorPluginType';
import { StreamCommandQueue } from './editor-actions/streamCommandQueue';

export const aiStreamingOrchestratorPlugin: AiStreamingOrchestratorPlugin = ({ api, config }) => {
	if (!api) {
		throw new Error('api is not defined');
	}

	let streamQueue = fg('platform_editor_free_gen_session')
		? undefined
		: new StreamCommandQueue(api, config?.streamFlowPacerEnabled ?? true);

	// Only allow one session to be active at a time
	let sessionId: string | null = null;

	return {
		name: 'aiStreamingOrchestrator',
		actions: {
			startSession: () => {
				if (!fg('platform_editor_free_gen_session')) {
					throw new Error('startSession is not supported!');
				}
				streamQueue = new StreamCommandQueue(api, config?.streamFlowPacerEnabled ?? true);
				sessionId = crypto.randomUUID();
				api.aiExperience.actions.startSession(sessionId);
				return sessionId;
			},

			endSession: async (error?: StreamingError) => {
				if (!fg('platform_editor_free_gen_session')) {
					throw new Error('endSession is not supported!');
				}
				if (sessionId === null) {
					throw new Error('endSession requires a session to be started first!');
				}

				if (error) {
					const { send } = api.aiExperience.actions.getAiExperienceActor();
					send({
						type: 'error',
						errorInfo: {
							failureReason: error.failureReason,
							statusCode: error.statusCode,
						},
					});
				}
				// Since `endSession` is going to be called after all the actions are invoked
				// so we can basically wait for the queue to be empty
				await streamQueue?.onEmptyQueue();
				// https://atlassian.slack.com/archives/C0962DYFLRL/p1759397599541949?thread_ts=1759366160.706849&cid=C0962DYFLRL
				// streamQueue?.destroy();
				api.aiExperience.actions.endSession(sessionId);
				sessionId = null;
			},

			abortAll: () => {
				sessionId = null;
				streamQueue?.abort();
			},

			initAction: (command) => {
				if (sessionId === null && fg('platform_editor_free_gen_session')) {
					throw new Error('initAction requires a session to be started first!');
				}
				const actionId = crypto.randomUUID();
				streamQueue?.initStreamCommand(actionId, command);
				return actionId;
			},

			streamValue: (actionId: string, chunk: string) => {
				// Use the queue manager to handle streaming
				// This will either process immediately if actor is ready, or queue for later
				streamQueue?.queueStreamCommand(actionId, chunk);
			},

			finishAction: async (actionId: string, error?: StreamingError) => {
				const { send } = api.aiExperience.actions.getAiExperienceActor();
				if (error) {
					send({
						type: 'error',
						errorInfo: {
							failureReason: error.failureReason,
							statusCode: error.statusCode,
						},
					});
				} else {
					await streamQueue?.endStreamCommand(actionId);
				}
				if (!fg('platform_editor_free_gen_session')) {
					await streamQueue?.onEmptyQueue();
				}
			},
		},
		usePluginHook: () => {
			useEffect(() => {
				return () => {
					streamQueue?.destroy();
				};
			}, []);
		},
	};
};
